import { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { EventsSubTabs } from '@/components/events/EventsSubTabs'
import { DISCIPLINE_LABELS } from '@/components/events/EventFormModal'
import { RequestStatusBadge } from '@/components/requests/RequestStatusBadge'
import { fmtDate } from '@/lib/dates'
import { useAuthStore } from '@/store/authStore'
import {
  useMissingCosts,
  useProfitability,
  type ProfitabilityRow,
} from '@/hooks/useEvents'
import {
  useDeleteRequest,
  useProductRequests,
  useUpdateRequest,
  type ProductRequest,
} from '@/hooks/useProductRequests'

// View C — Event Intelligence. Ships Section 1 (Profitability, with venue /
// discipline filters and totals) plus the missing-costs worklist and the show
// requests log. Later phases add: Revenue Totals by Location/Week, Event DNA,
// Stockout History (spec §View C 2-5).

const money = (v: number) =>
  v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

const NET_EXCLUDED_TITLE = 'Events without entered costs are excluded from the net total'

interface GroupTotal {
  name: string
  count: number
  revenue: number
  grossMargin: number
  net: number
  excluded: number
}

function groupTotals(
  list: ProfitabilityRow[],
  keyOf: (r: ProfitabilityRow) => string,
): GroupTotal[] {
  const map = new Map<string, GroupTotal>()
  for (const r of list) {
    const name = keyOf(r)
    let g = map.get(name)
    if (!g) {
      g = { name, count: 0, revenue: 0, grossMargin: 0, net: 0, excluded: 0 }
      map.set(name, g)
    }
    g.count += 1
    g.revenue += r.total_revenue
    g.grossMargin += r.total_gross_margin
    if (r.net_profit == null) g.excluded += 1
    else g.net += r.net_profit
  }
  return [...map.values()].sort((a, b) => b.revenue - a.revenue)
}

type SortKey =
  | 'location_name'
  | 'event_date'
  | 'discipline'
  | 'total_revenue'
  | 'offline_revenue'
  | 'total_gross_margin'
  | 'total_event_cost'
  | 'net_profit'
  | 'profit_margin_pct'
  | 'margin_per_cost_dollar'

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: 'location_name', label: 'Location', numeric: false },
  { key: 'event_date', label: 'Date', numeric: false },
  { key: 'discipline', label: 'Discipline', numeric: false },
  { key: 'total_revenue', label: 'Total Revenue', numeric: true },
  { key: 'offline_revenue', label: 'Offline', numeric: true },
  { key: 'total_gross_margin', label: 'Gross Margin', numeric: true },
  { key: 'total_event_cost', label: 'Event Cost', numeric: true },
  { key: 'net_profit', label: 'Net Profit', numeric: true },
  { key: 'profit_margin_pct', label: 'Margin %', numeric: true },
  { key: 'margin_per_cost_dollar', label: '$/cost-$', numeric: true },
]

// Click-to-edit note on a logged show request. Rachel reads this list when
// buying, so a note has to be editable after the fact without reopening the
// event — save on blur or Ctrl/Cmd+Enter, Escape cancels.
function NotesCell({ request }: { request: ProductRequest }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  // Escape unmounts the textarea, which doesn't reliably fire blur — this
  // guard keeps a stray save from overwriting the cancel.
  const cancelled = useRef(false)
  const updateNotes = useUpdateRequest()

  const saved = request.notes ?? ''
  // While the PATCH is in flight the list still holds the old note; show the
  // draft so the cell doesn't flash back to the previous text.
  const shown = updateNotes.isPending ? draft.trim() : saved

  function save() {
    setEditing(false)
    if (cancelled.current) {
      cancelled.current = false
      return
    }
    const next = draft.trim()
    if (next === saved) return
    updateNotes.mutate(
      { id: request.id, patch: { notes: next } },
      {
        onError: (err) => {
          setDraft(saved)
          toast.error(err instanceof Error ? err.message : 'Failed to save note')
        },
      },
    )
  }

  if (editing) {
    return (
      <textarea
        autoFocus
        rows={2}
        className="w-full border border-[var(--line-input)] bg-[var(--surface-card)] px-1 py-0.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand)]"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            cancelled.current = true
            setDraft(saved)
            setEditing(false)
          } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
      />
    )
  }

  return (
    <button
      type="button"
      className="w-full text-left whitespace-normal break-words hover:bg-[var(--surface-page)]"
      title="Click to edit"
      onClick={() => {
        setDraft(saved)
        setEditing(true)
      }}
    >
      {shown || <span className="text-[var(--mute)]">Add note…</span>}
    </button>
  )
}

export default function EventsIntelligence() {
  const { data: profitability, isLoading } = useProfitability()
  const { data: missingCosts } = useMissingCosts()
  // Event-origin rows only — the full pipeline (all origins, filters, PO
  // linkage) lives on the Product Requests page.
  const { data: requestsData } = useProductRequests({ originated_from: 'EVENT' })

  const [sortKey, setSortKey] = useState<SortKey>('net_profit')
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc')
  const [venue, setVenue] = useState('')
  const [discipline, setDiscipline] = useState('')

  function onSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const rows = useMemo(() => {
    const list = profitability?.events ?? []
    const dir = sortDir === 'desc' ? -1 : 1
    return [...list].sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (typeof av === 'string' || typeof bv === 'string') {
        return dir * String(av ?? '').localeCompare(String(bv ?? ''))
      }
      // Numbers; null (profit not computable yet) always sorts last.
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      return dir * (av - bv)
    })
  }, [profitability, sortKey, sortDir])

  const facets = useMemo(() => {
    const list = profitability?.events ?? []
    return {
      venues: [...new Set(list.map((r) => r.location_name))].sort(),
      disciplines: [...new Set(list.map((r) => r.discipline))].sort(),
    }
  }, [profitability])

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          (venue === '' || r.location_name === venue) &&
          (discipline === '' || r.discipline === discipline),
      ),
    [rows, venue, discipline],
  )

  const totals = useMemo(() => {
    const netRows = filteredRows.filter((r) => r.net_profit != null)
    const net = netRows.reduce((s, r) => s + (r.net_profit ?? 0), 0)
    // Margin % only makes sense against the revenue that actually produced the
    // net — events with no costs entered contribute revenue but no profit.
    const netRevenue = netRows.reduce((s, r) => s + r.total_revenue, 0)
    return {
      count: filteredRows.length,
      revenue: filteredRows.reduce((s, r) => s + r.total_revenue, 0),
      offline: filteredRows.reduce((s, r) => s + r.offline_revenue, 0),
      grossMargin: filteredRows.reduce((s, r) => s + r.total_gross_margin, 0),
      eventCost: filteredRows.reduce((s, r) => s + r.total_event_cost, 0),
      net,
      excluded: filteredRows.length - netRows.length,
      marginPct: netRevenue !== 0 ? (net / netRevenue) * 100 : null,
    }
  }, [filteredRows])

  const byVenue = useMemo(() => groupTotals(filteredRows, (r) => r.location_name), [filteredRows])
  const byDiscipline = useMemo(
    () => groupTotals(filteredRows, (r) => DISCIPLINE_LABELS[r.discipline] ?? r.discipline),
    [filteredRows],
  )

  // Requests logged before the dropdown excluded Brewster still carry it as
  // their event location; Brewster is the transfer origin, never an event.
  const requests = (requestsData ?? []).filter(
    (r) => !(r.event_location_name ?? '').toLowerCase().includes('brewster'),
  )
  const deleteRequest = useDeleteRequest()
  const user = useAuthStore((s) => s.user)
  // Same gate as the Product Requests table: only Gina / admins can destroy a
  // logged request.
  const canDelete =
    user != null && (user.username?.toLowerCase() === 'gina' || user.role === 'admin')

  async function onDeleteRequest(r: ProductRequest) {
    if (!window.confirm(
      `Delete request "${r.product_title}"? It disappears from this list for everyone.`
    )) return
    try {
      await deleteRequest.mutateAsync(r.id)
      toast.success('Request deleted')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  const selectCls =
    'h-7 border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-xs focus:outline-none focus:border-[var(--brand)]'

  function netProfitCell(r: ProfitabilityRow) {
    if (r.net_profit == null) return <span className="text-[var(--mute)]">N/A</span>
    return <span className="tabular-nums">{money(r.net_profit)}</span>
  }

  const variants = missingCosts?.variants ?? []

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      <div className="flex-1 overflow-auto p-4 max-w-6xl w-full mx-auto">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h1 className="text-base font-semibold">Event Intelligence</h1>
          <div className="flex-1" />
          <EventsSubTabs />
        </div>

        {/* Section nav — more sections join as later phases ship. Buttons with
            scrollIntoView: hash anchors can't scroll this inner container. */}
        <div className="flex items-center gap-2 mb-3 text-xs">
          <button
            className="px-2 py-0.5 border border-[var(--line-warm)] bg-[var(--surface-card)] text-[var(--text-primary)] hover:bg-[var(--surface-page)]"
            onClick={() => document.getElementById('profitability')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            Profitability
          </button>
          <button
            className="px-2 py-0.5 border border-[var(--line-warm)] bg-[var(--surface-card)] text-[var(--text-primary)] hover:bg-[var(--surface-page)]"
            onClick={() => document.getElementById('missing-costs')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            Missing Costs
          </button>
          <button
            className="px-2 py-0.5 border border-[var(--line-warm)] bg-[var(--surface-card)] text-[var(--text-primary)] hover:bg-[var(--surface-page)]"
            onClick={() => document.getElementById('requests')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            Show Requests
          </button>
          <span className="text-[var(--mute)]">
            Revenue Totals · Event DNA · Stockouts — coming in later phases
          </span>
        </div>

        <h2 id="profitability" className="text-sm font-semibold mb-1">Event profitability ranking</h2>
        <p className="text-xs text-[var(--mute)] mb-2">
          Ranked on gross margin, not revenue. Net profit shows N/A until booth, labor, and travel
          costs are entered in Event Admin. Click a column to sort.
        </p>

        {rows.length > 0 && (
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <select className={selectCls} value={venue} onChange={(e) => setVenue(e.target.value)}>
              <option value="">All venues</option>
              {facets.venues.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select
              className={selectCls}
              value={discipline}
              onChange={(e) => setDiscipline(e.target.value)}
            >
              <option value="">All disciplines</option>
              {facets.disciplines.map((d) => (
                <option key={d} value={d}>{DISCIPLINE_LABELS[d] ?? d}</option>
              ))}
            </select>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-1 mb-6">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-8 text-center text-xs text-[var(--mute)] mb-6">
            No synced events yet — register and sync events in Event Admin first.
          </div>
        ) : (
          <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)] mb-6">
            <table className="text-xs w-full">
              <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)]">
                <tr className="text-left text-[var(--mute)]">
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={`tbl-th ${c.numeric ? 'text-right' : ''}`}>
                      <button
                        className="inline-flex items-center gap-0.5 hover:text-[var(--text-primary)]"
                        onClick={() => onSort(c.key)}
                      >
                        {c.label}
                        {sortKey === c.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && (
                  <tr className="border-b border-[var(--line-warm)]">
                    <td colSpan={COLUMNS.length} className="px-2 py-6 text-center text-[var(--mute)]">
                      No events match these filters.
                    </td>
                  </tr>
                )}
                {filteredRows.map((r, i) => (
                  <tr key={r.event_id} className={`border-b border-[var(--line-warm)] ${i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'}`}>
                    <td className="px-2 py-1">{r.location_name}</td>
                    <td className="px-2 py-1 tabular-nums whitespace-nowrap">
                      {fmtDate(r.event_date)}
                      {!r.synced && <span className="text-[var(--mute)]"> (not synced)</span>}
                    </td>
                    <td className="px-2 py-1">{DISCIPLINE_LABELS[r.discipline] ?? r.discipline}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{money(r.total_revenue)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {/* Opens that week on Analysis, where the entries are listed and edited. */}
                      {r.offline_revenue > 0 ? (
                        <Link
                          to={`/events?event=${r.event_id}`}
                          className="text-[var(--brand)] hover:underline"
                        >
                          {money(r.offline_revenue)}
                        </Link>
                      ) : (
                        <span className="text-[var(--mute)]">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">
                      {money(r.total_gross_margin)}
                      {r.missing_cost_count > 0 && (
                        <span
                          className="text-[var(--status-awaiting-fg)] ml-1"
                          title={`${r.missing_cost_count} sold variant(s) missing unit cost in Shopify — margin understated`}
                        >
                          ⚠ {r.missing_cost_count}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{money(r.total_event_cost)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{netProfitCell(r)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {r.profit_margin_pct != null
                        ? `${r.profit_margin_pct.toFixed(1)}%`
                        : <span className="text-[var(--mute)]">N/A</span>}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {r.margin_per_cost_dollar != null
                        ? r.margin_per_cost_dollar.toFixed(2)
                        : <span className="text-[var(--mute)]">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[var(--line-warm)] font-semibold">
                  <td className="px-2 py-1">{totals.count} event(s)</td>
                  <td className="px-2 py-1" />
                  <td className="px-2 py-1" />
                  <td className="px-2 py-1 text-right tabular-nums">{money(totals.revenue)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{money(totals.offline)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{money(totals.grossMargin)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{money(totals.eventCost)}</td>
                  <td
                    className="px-2 py-1 text-right tabular-nums"
                    title={totals.excluded > 0 ? NET_EXCLUDED_TITLE : undefined}
                  >
                    {money(totals.net)}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {totals.marginPct != null
                      ? `${totals.marginPct.toFixed(1)}%`
                      : <span className="text-[var(--mute)]">N/A</span>}
                  </td>
                  <td className="px-2 py-1" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {filteredRows.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-1">Totals by venue &amp; discipline</h3>
            <p className="text-xs text-[var(--mute)] mb-2">
              Same filtered events, rolled up. Net profit skips events with no costs entered yet.
            </p>
            <div className="flex gap-4 flex-wrap">
              {[
                { heading: 'Venue', groups: byVenue },
                { heading: 'Discipline', groups: byDiscipline },
              ].map((block) => (
                <div
                  key={block.heading}
                  className="flex-1 min-w-[320px] overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)]"
                >
                  <table className="text-xs w-full">
                    <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)]">
                      <tr className="text-left text-[var(--mute)]">
                        <th className="tbl-th">{block.heading}</th>
                        <th className="tbl-th text-right">Events</th>
                        <th className="tbl-th text-right">Revenue</th>
                        <th className="tbl-th text-right">Gross Margin</th>
                        <th className="tbl-th text-right">Net Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {block.groups.map((g, i) => (
                        <tr key={g.name} className={`border-b border-[var(--line-warm)] ${i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'}`}>
                          <td className="px-2 py-1">{g.name}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{g.count}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{money(g.revenue)}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{money(g.grossMargin)}</td>
                          <td
                            className="px-2 py-1 text-right tabular-nums"
                            title={g.excluded > 0 ? NET_EXCLUDED_TITLE : undefined}
                          >
                            {money(g.net)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        )}

        <h2 id="missing-costs" className="text-sm font-semibold mb-1">Variants missing unit cost</h2>
        <p className="text-xs text-[var(--mute)] mb-2">
          These variants sold at events but have no unit cost in Shopify, so their COGS counts as
          $0 and event margins read high. Rachel / Gina: fill in “Cost per item” on the Shopify
          variant, then re-sync the affected events.
        </p>

        {variants.length === 0 ? (
          <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-4 text-center text-xs text-[var(--mute)] mb-6">
            All sold variants have costs — margins are fully covered.
          </div>
        ) : (
          <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)] mb-6">
            <table className="text-xs w-full">
              <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)]">
                <tr className="text-left text-[var(--mute)]">
                  <th className="tbl-th">SKU</th>
                  <th className="tbl-th">Product</th>
                  <th className="tbl-th">Variant</th>
                  <th className="tbl-th">Brand</th>
                  <th className="tbl-th text-right">Events</th>
                  <th className="tbl-th text-right">Units sold</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v, i) => (
                  <tr key={v.variant_id} className={`border-b border-[var(--line-warm)] ${i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'}`}>
                    <td className="px-2 py-1 tabular-nums">{v.sku ?? '—'}</td>
                    <td className="px-2 py-1">{v.product_title ?? '—'}</td>
                    <td className="px-2 py-1">{v.variant_title ?? '—'}</td>
                    <td className="px-2 py-1">{v.vendor ?? '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{v.event_count}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{v.total_qty_sold}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-baseline gap-2 mb-1">
          <h2 id="requests" className="text-sm font-semibold">Show requests</h2>
          <Link
            to="/product-requests"
            className="text-xs text-[var(--brand)] hover:underline"
            style={{ textDecoration: 'none' }}
          >
            View in Product Requests →
          </Link>
        </div>
        <p className="text-xs text-[var(--mute)] mb-2">
          Things customers asked for at the booth that we didn&apos;t have. Logged during the show,
          so read this alongside the pack list before the next event at the same venue. Sourcing and
          follow-up happen on the Product Requests page.
        </p>

        {requests.length === 0 ? (
          <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] px-2 py-4 text-center text-xs text-[var(--mute)]">
            No requests logged yet — log one from the Requests tab (Originated from: Event).
          </div>
        ) : (
          <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)]">
            <table className="text-xs w-full">
              <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)]">
                <tr className="text-left text-[var(--mute)]">
                  <th className="tbl-th">Logged</th>
                  <th className="tbl-th">Event</th>
                  <th className="tbl-th">Product</th>
                  <th className="tbl-th text-right">Qty</th>
                  <th className="tbl-th">Status</th>
                  <th className="tbl-th">Notes</th>
                  <th className="tbl-th">By</th>
                  <th className="tbl-th" />
                </tr>
              </thead>
              <tbody>
                {requests.map((r, i) => (
                  <tr key={r.id} className={`border-b border-[var(--line-warm)] align-top ${i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'}`}>
                    <td className="px-2 py-1 tabular-nums whitespace-nowrap">
                      {fmtDate(r.created_at)}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {r.event_location_name ?? '—'}
                    </td>
                    <td className="px-2 py-1">{r.product_title}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.qty}</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <RequestStatusBadge status={r.status} />
                    </td>
                    <td className="px-2 py-1 text-[var(--mute)]">
                      {/* Wrapper carries the width: a note is a sentence or
                          two and must render in full, not truncate. */}
                      <div className="min-w-[200px] max-w-[400px]">
                        <NotesCell request={r} />
                      </div>
                    </td>
                    <td className="px-2 py-1">{r.created_by ?? '—'}</td>
                    <td className="px-2 py-1 text-right">
                      {canDelete && (
                        <button
                          aria-label="Delete request"
                          onClick={() => onDeleteRequest(r)}
                          className="p-1 text-[var(--mute)] hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
